import { Flex } from "@chakra-ui/react";
import { CommentForm } from "./CommentForm";
import { useHydrateComments } from "../../hooks/useHydrateComments";
import { SLIDE_IN_MENUS } from "../../../root/constants";
import { SlideInHeader } from "../../../../components/slideInMenu/components/SlideInHeader";
import { useMemo } from "react";
import {
  GET_RELEASE_COMMMENTS,
  postReleaseComment,
  toggleCommentReaction,
  editReleaseComment,
  deleteReleaseComment,
} from "../../api/readerScreenApi";
import { useReleaseData } from "../../hooks/hookstate/useReleaseData";
import { useReleaseCommentsData } from "../../hooks/hookstate/useRelatedReleases";
import { Comment } from "../../../../components/comment/Comment";
import { BlogCommentEntity } from "../../../../types";
import { useSendComment } from "../../../../hooks/comments/useSendComment";
import { useCommentReactions } from "../../../../hooks/comments/useCommentReactions";
import { useEditComment } from "../../../../hooks/comments/useEditComment";
import { useDeleteComment } from "../../../../hooks/comments/useDeleteComment";
import { CommentsDisabledLarge } from "../../../../components/comment/CommentsDisabledLarge";
import { useAccountData } from "../../../../hooks/useAccountData";
import { useUserData } from "../../../../hooks/useUserData";

interface CommentsMenuProps {
  onCloseMenu?: () => void;
}
export function CommentsMenuWrapper(props: CommentsMenuProps) {
  useHydrateComments();
  return <CommentsMenu {...props} />;
}

export function CommentsMenu({ onCloseMenu }: CommentsMenuProps) {
  const accountData = useAccountData();
  const { userData } = useUserData();
  const disableCommentingFlag = accountData?.features["disable_commenting"];
  const { releaseData } = useReleaseData();
  const queryKey = useMemo(() => {
    return [...GET_RELEASE_COMMMENTS, releaseData?.id ?? 0];
  }, [releaseData]);

  const { releaseCommentsData } = useReleaseCommentsData();

  const sendComment = useSendComment(queryKey, (data) =>
    postReleaseComment(releaseData?.id ?? 0, data),
  );

  const reactToComment = useCommentReactions(
    queryKey,
    ({ reactionTypeId, comment }) => {
      return toggleCommentReaction(
        comment.object_id,
        comment.id,
        reactionTypeId,
      );
    },
  );

  const editComment = useEditComment(queryKey, ({ body, comment }) => {
    return editReleaseComment({
      releaseId: comment.object_id,
      commentId: comment.id,
      body,
    });
  });

  const deleteComment = useDeleteComment(
    queryKey,
    ({ articleId, commentId }) => {
      return deleteReleaseComment({
        releaseId: articleId,
        commentId,
      });
    },
  );

  const areCommentsDisabled =
    !(releaseData?.is_open ?? false) ||
    (!!disableCommentingFlag && !userData?.gold);

  return (
    <>
      <SlideInHeader
        openMenu={SLIDE_IN_MENUS.comments}
        onCloseMenu={onCloseMenu}
      />
      <Flex
        className="styled-scrollbar"
        overflowY="auto"
        overflowX="hidden"
        pr="12px"
        pl="24px"
        pb="24px"
        pt="0"
        maxWidth="100%"
        flexGrow={1}
        width="100%"
        flexDirection="column-reverse"
        position="relative"
        justifyContent={
          !releaseCommentsData || releaseCommentsData.length === 0
            ? "center"
            : "flex-start"
        }
        gap="24px"
      >
        {areCommentsDisabled && <CommentsDisabledLarge />}
        {(releaseCommentsData ?? [])
          .map((comment) => (
            <Comment
              key={comment.id}
              comment={comment as BlogCommentEntity}
              submitReply={(body, parentComment) => {
                sendComment({
                  body,
                  parentComment: parentComment as BlogCommentEntity,
                });
              }}
              reactToComment={(comment, reactionTypeId) =>
                reactToComment({
                  reactionTypeId,
                  comment,
                })
              }
              editComment={(comment, body) =>
                editComment({
                  comment,
                  body,
                })
              }
              deleteComment={(comment) => deleteComment(comment)}
              compact
              contentEditable
              commentsDisabled={areCommentsDisabled}
            />
          ))
          .reverse()}
      </Flex>
      <CommentForm
        postReleaseComment={(body) =>
          sendComment({
            body,
          })
        }
        areCommentsDisabled={areCommentsDisabled}
      />
    </>
  );
}
